import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DeleteDialog from '../../components/DeleteDialog';
import { useAuth } from "../../context/AuthContext"; 
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Table,
  Modal,
  Card,
} from 'react-bootstrap';
import { Formik, Field, Form as FormikForm, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// ---- Constants ----
const BASE_URL = process.env.REACT_APP_BASE_URL;

const API_URL = `${BASE_URL}/api/lab/lab-tests`;
const COMBO_URL = `${BASE_URL}/api/lab/lab-test-combos`;

// ---- Reusable validation ----
const requiredTrimmedString = (msg) =>
  Yup.string()
    .required(msg)
    .test(
      'no-empty-or-spaces',
      `${msg} cannot be empty or just spaces`,
      (value) => value && value.trim().length > 0 && !/^\s+$/.test(value)
    );

// Validation Schemas
const labTestValidation = Yup.object().shape({
  name: requiredTrimmedString('Test Name'),
  type: requiredTrimmedString('Test Type'),
});
const comboValidation = Yup.object().shape({
  name: requiredTrimmedString('Combo Name'),
  testIds: Yup.array()
    .min(1, 'Select at least one test')
    .of(Yup.number().required()),
});

// ---- Prevent spaces helpers ----
const preventSpaces = (e) => {
  if (
    e.key === ' ' &&
    (!e.target.value || /^\s*$/.test(e.target.value + e.key))
  ) {
    e.preventDefault();
    return false;
  }
  return true;
};
const preventPasteSpaces = (e) => {
  if (/^\s*$/.test(e.clipboardData.getData('text'))) {
    e.preventDefault();
  }
};

// ---- Reusable field ----
const FormField = ({ name, label, ...props }) => (
  <Form.Group>
    <Form.Label>{label}</Form.Label>
    <Field
      name={name}
      as={Form.Control}
      required
      onKeyDown={preventSpaces}
      onPaste={preventPasteSpaces}
      {...props}
    />
    <ErrorMessage name={name} component="div" className="text-danger small" />
  </Form.Group>
);

const AddLabTest = () => {
  const [tests, setTests] = useState([]);
  const [combos, setCombos] = useState([]);
  const [editId, setEditId] = useState(null);
  const [showComboModal, setShowComboModal] = useState(false);
  const [editComboId, setEditComboId] = useState(null);
  const { user } = useAuth(); 
  // ---- Fetchers ----
 const fetchTests = useCallback(async () => {
  try {
    const res = await axios.get(API_URL, {
      params: {
        hospital_id: user.hospital_id,
        subhospital_id: user.subhospital_id, // agar subhospital wise filter chahiye
      }
    });
    setTests(res.data);
  } catch (err) {
    setTests([]);
  }
}, [user.hospital_id, user.subhospital_id]);

const fetchCombos = useCallback(async () => {
  try {
    const res = await axios.get(COMBO_URL, {
      params: {
        hospital_id: user.hospital_id,
        subhospital_id: user.subhospital_id,
      }
    });
    setCombos(res.data);
  } catch (err) {
    setCombos([]);
  }
}, [user.hospital_id, user.subhospital_id]);


  useEffect(() => {
    fetchTests();
    fetchCombos();
  }, [fetchTests, fetchCombos]);

  // ---- Edit handlers ----
  const handleEdit = (test) => setEditId(test.id);
  const handleEditCombo = (combo) => {
    setEditComboId(combo.id);
    setShowComboModal(true);
  };

  // ---- Table Rows Merging ----
  const mergedRows = [
    ...tests.map((test) => ({
      type: 'test',
      id: test.id,
      name: test.name,
      testType: test.type,
      tests: [],
    })),
    ...combos.map((combo) => ({
      type: 'combo',
      id: combo.id,
      name: combo.name,
      testType: '',
      tests: combo.tests,
    })),
  ];

  return (
    <div className="bg-light min-vh-100">
      <Container fluid className="py-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h3 className="global-heading mb-0 ">
              {editId ? 'Edit Lab Test' : 'Add Lab Test'}
            </h3>
          </Col>
          <Col xs="auto" className="text-end">
            <Button
              size="sm"
              className="global-btn"
              onClick={() => {
                setShowComboModal(true);
                setEditComboId(null);
              }}
            >
              + Combo
            </Button>
          </Col>
        </Row>

        {/* Lab Test Form */}
        <Card className="shadow-sm border-0 mb-4">
          <Card.Body>
            <Formik
              enableReinitialize
              initialValues={
                editId
                  ? tests.find((t) => t.id === editId) || { name: '', type: '' }
                  : { name: '', type: '' }
              }
              validationSchema={labTestValidation}
              onSubmit={async (values, { resetForm }) => {
                try {
                  if (editId) {
                    await axios.put(`${API_URL}/${editId}`, values);
                  } else {
                    await axios.post(API_URL, {
                    ...values,
                    hospitalId: user.hospital_id,
                  });
                  }
                  resetForm();
                  setEditId(null);
                  fetchTests();
                } catch (err) {
                  // Optionally, add toast/error
                }
              }}
            >
              {({ handleSubmit, resetForm }) => (
                <FormikForm onSubmit={handleSubmit}>
                  <Row className="align-items-end g-3">
                    <Col md={4}>
                      <FormField name="name" label="Test Name" placeholder="Enter test name" />
                    </Col>
                    <Col md={4}>
                      <FormField name="type" label="Test Type" placeholder="Enter test type" />
                    </Col>
                    <Col md={4} className="d-flex gap-2">
                      <Button
                        type="submit"
                        variant="light"
                        size="sm"
                        className="global-btn fw-normal d-flex align-items-center"
                      >
                        <span className="material-icons me-1" style={{ fontSize: 16 }}>
                          {editId ? "check_circle" : "add_circle"}
                        </span>
                        {editId ? "Update" : "Add"}
                      </Button>
                      {editId && (
                        <Button
                          type="button"
                          variant="light"
                          size="sm"
                          className="global-btn fw-normal d-flex align-items-center"
                          onClick={() => {
                            setEditId(null);
                            resetForm();
                          }}
                        >
                          <span className="material-icons me-1" style={{ fontSize: 16 }}>
                            cancel
                          </span>
                          Cancel
                        </Button>
                      )}
                    </Col>
                  </Row>
                </FormikForm>
              )}
            </Formik>
          </Card.Body>
        </Card>

        {/* Combo Modal */}
        <Modal
          show={showComboModal}
          onHide={() => {
            setShowComboModal(false);
            setEditComboId(null);
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editComboId ? 'Edit Combo' : 'Create Combo'}
            </Modal.Title>
          </Modal.Header>
          <Formik
            enableReinitialize
            initialValues={
              editComboId
                ? {
                    name: combos.find((c) => c.id === editComboId)?.name || '',
                    testIds:
                      combos.find((c) => c.id === editComboId)?.tests.map((t) => t.id) || [],
                  }
                : { name: '', testIds: [] }
            }
            validationSchema={comboValidation}
            onSubmit={async (values, { resetForm }) => {
              try {
                if (editComboId) {
                  await axios.put(`${COMBO_URL}/${editComboId}`, values);
                } else {
               await axios.post(COMBO_URL, {
                ...values,
                hospitalId: user.hospital_id,
                subhospitalId: user.subhospital_id
              });
                }
                resetForm();
                setShowComboModal(false);
                setEditComboId(null);
                fetchCombos();
              } catch (err) {
                // Optionally, add toast/error
              }
            }}
          >
            {({ handleSubmit, values, setFieldValue, resetForm }) => (
              <FormikForm onSubmit={handleSubmit}>
                <Modal.Body>
                  <FormField name="name" label="Combo Name" placeholder="Combo Name" />
                  <Form.Group className="mb-3">
                    <Form.Label>Select Lab Tests</Form.Label>
                    <div className="border rounded p-2 bg-light" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                      {tests.map((test) => (
                        <Form.Check
                          key={test.id}
                          type="checkbox"
                          id={`combo-test-${test.id}`}
                          label={
                            <span className="fw-normal">
                              {test.name} <span className="text-muted">({test.type})</span>
                            </span>
                          }
                          value={test.id}
                          checked={values.testIds.includes(test.id)}
                          onChange={(e) => {
                            const val = test.id;
                            setFieldValue(
                              'testIds',
                              e.target.checked
                                ? [...values.testIds, val]
                                : values.testIds.filter((id) => id !== val)
                            );
                          }}
                        />
                      ))}
                    </div>
                    <ErrorMessage
                      name="testIds"
                      component="div"
                      className="text-danger small"
                    />
                  </Form.Group>
                  <Row className="justify-content-end">
                    <Col xs="auto">
                      <Button
                        type="submit"
                        variant="light"
                        size="sm"
                        className="global-btn fw-normal d-flex align-items-center"
                      >
                        <span className="material-icons me-1" style={{ fontSize: 16 }}>
                          {editComboId ? "check_circle" : "add_circle"}
                        </span>
                        {editComboId ? "Update" : "Add"}
                      </Button>
                    </Col>
                    <Col xs="auto">
                      <Button
                        type="button"
                        variant="light"
                        size="sm"
                        className="global-btn fw-normal d-flex align-items-center"
                        onClick={() => {
                          setShowComboModal(false);
                          setEditComboId(null);
                          resetForm();
                        }}
                      >
                        <span className="material-icons me-1" style={{ fontSize: 16 }}>
                          cancel
                        </span>
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                </Modal.Body>
              </FormikForm>
            )}
          </Formik>
        </Modal>

        {/* Table */}
        <Card className="shadow-sm border-0">
          <Card.Body>
            <Table responsive bordered hover className="align-middle">
              <thead className="bg-primary text-white">
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Type / Tests</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mergedRows.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center text-secondary">
                      No lab tests or combos added yet.
                    </td>
                  </tr>
                ) : (
                  mergedRows.map((row, idx) => (
                    <tr key={row.type + row.id}>
                      <td>{idx + 1}</td>
                      <td className="fw-semibold">{row.name}</td>
                      <td>
                        {row.type === 'test'
                          ? <span className="text-muted">{row.testType}</span>
                          : (
                            <div className="d-flex flex-wrap gap-2">
                              {row.tests.map((t) => (
                                <span key={t.id} className="border px-2 py-1 rounded text-muted bg-light">
                                  {t.name} <span className="text-secondary">({t.type})</span>
                                </span>
                              ))}
                            </div>
                          )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="light"
                            size="sm"
                            onClick={() => row.type === 'test' ? handleEdit(row) : handleEditCombo(row)}
                          >
                            <span className="material-icons me-1" style={{ fontSize: 16 ,color: 'var(--primary-color)' }}>
                              edit
                            </span>
                          </Button>
                          <DeleteDialog
                            path={
                              row.type === 'test'
                                ? `${API_URL}/${row.id}`
                                : `${COMBO_URL}/${row.id}`
                            }
                            message={
                              row.type === 'test'
                                ? "Test deleted successfully"
                                : "Combo deleted successfully"
                            }
                            onSuccess={() => {
                              fetchTests();
                              fetchCombos();
                            }}
                            icon="button"
                            btnProps={{
                              size: "sm",
                              variant: "light",
                              className: "fw-normal d-flex align-items-center",
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AddLabTest;